import { useState, useRef, useEffect } from "react";
import { Sun, Moon, Monitor, Check } from "lucide-react";
import { useTheme } from "@/hooks/useTheme";
import { cn } from "@/lib/utils";

const OPTIONS = [
  { value: "light", label: "Light", icon: Sun },
  { value: "dark", label: "Dark", icon: Moon },
  { value: "system", label: "System", icon: Monitor },
];

export const ThemeToggle = () => {
  const { preference, applied, setTheme } = useTheme();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const ActiveIcon =
    preference === "system" ? Monitor : applied === "dark" ? Moon : Sun;

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-label="Toggle theme"
        data-testid="theme-toggle-button"
        className={cn(
          "h-9 w-9 rounded-full flex items-center justify-center border transition-colors",
          "bg-white/70 hover:bg-white border-zinc-200 text-zinc-700 hover:text-zinc-900",
          "dark:bg-zinc-900/70 dark:hover:bg-zinc-900 dark:border-white/10 dark:text-zinc-300 dark:hover:text-zinc-50",
          "backdrop-blur-sm"
        )}
      >
        <ActiveIcon className="h-4 w-4" strokeWidth={2} />
      </button>

      {open && (
        <div
          data-testid="theme-toggle-menu"
          className={cn(
            "absolute right-0 mt-2 w-40 rounded-2xl p-1.5 z-50 shadow-xl",
            "bg-white border border-zinc-200",
            "dark:bg-zinc-900 dark:border-white/10"
          )}
        >
          {OPTIONS.map((opt) => {
            const Icon = opt.icon;
            const active = preference === opt.value;
            return (
              <button
                key={opt.value}
                type="button"
                onClick={() => {
                  setTheme(opt.value);
                  setOpen(false);
                }}
                data-testid={`theme-option-${opt.value}`}
                className={cn(
                  "w-full flex items-center gap-2.5 px-3 py-2 rounded-xl text-sm font-medium transition-colors",
                  active
                    ? "bg-zinc-100 text-zinc-900 dark:bg-zinc-800 dark:text-zinc-50"
                    : "text-zinc-600 hover:bg-zinc-100 hover:text-zinc-900 dark:text-zinc-400 dark:hover:bg-zinc-800 dark:hover:text-zinc-50"
                )}
              >
                <Icon className="h-4 w-4" strokeWidth={2} />
                <span className="flex-1 text-left">{opt.label}</span>
                {active && <Check className="h-3.5 w-3.5" strokeWidth={3} />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};
